/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { NgClass } from "@angular/common";
import { Component, EventEmitter, forwardRef, Input, Output } from "@angular/core";
import {
	AbstractControl,
	ControlValueAccessor,
	FormControl,
	NG_VALIDATORS,
	NG_VALUE_ACCESSOR,
	ReactiveFormsModule,
	ValidationErrors,
	Validator
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDatepickerInputEvent, MatDatepickerModule } from "@angular/material/datepicker";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { dateValidator } from "../../directives/validators/date.directive";
import { TimePeriodRange } from "../../models/ui/time-period-range.model";
import { DATEPICKER_MIN_DATE } from "../../utils/constants";
import { DATE_FORMAT_REGEXP, formatDateWithMoment } from "../../utils/utils";

@Component({
	selector: "globalictcc-datepicker",
	standalone: true,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => DatepickerComponent),
			multi: true
		},
		{
			provide: NG_VALIDATORS,
			useExisting: forwardRef(() => DatepickerComponent),
			multi: true
		}
	],
	imports: [
		MatFormFieldModule,
		MatInputModule,
		MatDatepickerModule,
		MatButtonModule,
		ReactiveFormsModule,
		NgClass
	],
	templateUrl: "./datepicker.component.html",
	styleUrl: "./datepicker.component.scss"
})
export class DatepickerComponent implements ControlValueAccessor, Validator {
	@Input() public label: string = "";
	@Input() public range: boolean = true;
	@Input() public disabled: boolean = false;

	@Output() public handleDateChange = new EventEmitter<Date | TimePeriodRange | null>();

	public minDate: Date = DATEPICKER_MIN_DATE;
	public selection: Date | TimePeriodRange | null = null;
	public selectionStart: Date | null = null;
	public selectionEnd: Date | null = null;
	public isDisabled: boolean = false;
	public selectedDate = new FormControl<Date | null>(null, [dateValidator("min", this.minDate)]);
	public selectedStartDate = new FormControl<Date | null>(null, [
		dateValidator("min", this.minDate)
	]);
	public selectedEndDate = new FormControl<Date | null>(null);
	public isInvalid: boolean = false;
	public isInputDirty: boolean = false;

	/**
	 * Clears the current date selection by resetting the selected date, start date, and end date.
	 * Also sets the selection to null and triggers the change callback with the updated selection.
	 */
	public clearSelection() {
		this.selectedDate.reset();
		this.selectedStartDate.reset();
		this.selectedEndDate.reset();
		this.selectionEnd = null;
		this.selectionStart = null;

		this.isInvalid = false;
		this.isInputDirty = false;
		this.selection = null;

		this.handleDateChange.emit(this.selection);

		this._onChange(this.selection);
	}

	/**
	 * Handles the date change event from the datepicker input.
	 * Updates the selection with the new date and triggers the change callback.
	 *
	 * @param event - The date change event containing the new date value.
	 */
	public onDateChange(event: MatDatepickerInputEvent<Date>) {
		this.selection = formatDateWithMoment(event.value);

		this.handleDateChange.emit(this.selection);

		this._onChange(this.selection);
		this.onTouched();
	}

	/**
	 * Handles the input event for the date picker component.
	 *
	 * This method is triggered when the user inputs a date. It checks if the input value
	 * is valid according to the `DATE_FORMAT_REGEXP` regular expression.
	 * If the input is not valid, it sets the `isInputDirty` flag to `true`.
	 *
	 * Additionally, it calls the `onTouched` method to mark the component as touched
	 * and the `_onValidatorChange` method to trigger validation.
	 *
	 * @param event - The input event triggered by the user.
	 */
	public onDateInput(event: Event, rangeInput?: "start" | "end") {
		const inputValue = (event.target as HTMLInputElement).value;
		const formattedDate = inputValue
			? formatDateWithMoment(inputValue, rangeInput === "end")
			: null;

		this.isInputDirty = !!inputValue && !DATE_FORMAT_REGEXP.test(inputValue) && !!formattedDate;

		if (!this.isInputDirty) {
			if (this.range) {
				const range = {
					...(rangeInput === "start"
						? { start: formattedDate }
						: { start: this.selectionStart }),
					...(rangeInput === "end" ? { end: formattedDate } : { end: this.selectionEnd })
				};

				this.selection = range;

				this.handleDateChange.emit(range);
				this._onChange(range);
			} else {
				this.selection = formattedDate;

				this.handleDateChange.emit(formattedDate);
				this._onChange(formattedDate);
			}
		}

		this.onTouched();
	}

	/**
	 * Handles the change event for the start date in the date picker.
	 * Updates the `selectionStart` property with the selected date.
	 *
	 * @param event - The date picker input event containing the selected date.
	 */
	public onStartDateChange(event: MatDatepickerInputEvent<Date>) {
		this.selectionStart = formatDateWithMoment(event.value);
	}

	/**
	 * Handles the change event for the end date in the date picker.
	 *
	 * @param event - The date picker input event containing the selected end date.
	 */
	public onEndDateChange(event: MatDatepickerInputEvent<Date>) {
		this.selectionEnd = formatDateWithMoment(event.value, true);
	}

	/**
	 * Handles the selection of a date range.
	 * This method is triggered when a date range is selected.
	 * It calls the `_onChange` method with an object containing the start and end dates of the selection.
	 */
	public onRangeSelection() {
		const range = { start: this.selectionStart, end: this.selectionEnd };

		this.handleDateChange.emit(range);

		this._onChange(range);
		this.onTouched();
	}

	/**
	 * Writes the provided value to the selection.
	 *
	 * @param value - The date value to be written. It can be a Date object or null.
	 */
	public writeValue(value: Date | TimePeriodRange | null) {
		if (!value) return this.clearSelection();

		if (this.range) {
			const { start, end } = value as TimePeriodRange;

			const startMoment = formatDateWithMoment(start);
			const endMoment = end ? new Date(end) : null;
			const displayedEnd = endMoment ? new Date(endMoment.getTime() - 1) : null;

			this.selection = { start: startMoment, end: endMoment };
			this.selectedStartDate.setValue(startMoment);
			this.selectionStart = startMoment;
			this.selectedEndDate.setValue(displayedEnd);
			this.selectionEnd = endMoment;
		} else {
			const date = formatDateWithMoment(value as Date);

			this.selection = date;
			this.selectedDate.setValue(date);
		}

		this.handleDateChange.emit(this.selection);

		this._onChange(this.selection);
		this.onTouched();
	}

	/**
	 * Sets the disabled state of the datepicker component.
	 *
	 * @param isDisabled - A boolean value indicating whether the component should be disabled.
	 */
	public setDisabledState(isDisabled: boolean) {
		this.isDisabled = isDisabled;
	}

	/**
	 * Callback function that is triggered when the date value changes.
	 * It can handle a single Date object, an object with start and end Date properties, or null.
	 *
	 * @param value - The new date value. It can be:
	 *   - A single Date object
	 *   - An object with `start` and `end` properties, both of which can be Date or null
	 *   - Null
	 */
	private _onChange: (value: Date | TimePeriodRange | null) => void = () => {};

	/**
	 * Registers a callback function that is called when the value changes.
	 *
	 * @param fn - The callback function that receives the new value. The value can be:
	 * - A `Date` object representing a single date.
	 * - An object with `start` and `end` properties, each being a `Date` or `null`, representing a date range.
	 * - `null` if there is no value.
	 */
	public registerOnChange(fn: (value: Date | TimePeriodRange | null) => void) {
		this._onChange = fn;
	}

	/**
	 * A callback function that is called when the datepicker component is touched.
	 * This function is typically used to notify the Angular forms API that the component has been interacted with.
	 * It is initialized as a no-op function.
	 */
	public onTouched: () => void = () => {};

	/**
	 * Registers a callback function that will be called when the control is touched.
	 * This method is part of the ControlValueAccessor interface, which allows the component
	 * to act as a form control in Angular forms.
	 *
	 * @param fn - The callback function to register.
	 */
	public registerOnTouched(fn: () => void) {
		this.onTouched = fn;
	}

	/**
	 * Validates the given form control and updates the `isInvalid` property.
	 *
	 * @param control - The form control to validate.
	 * @returns An object containing validation errors if any, otherwise null.
	 */
	public validate(control: AbstractControl): ValidationErrors | null {
		const isSelectedStartDateInvalid = this.range && !!this.selectedStartDate.errors;
		const isSelectedEndDateInvalid = this.range && !!this.selectedEndDate.errors;
		const isSelectedDateInvalid = this.range
			? isSelectedStartDateInvalid || isSelectedEndDateInvalid
			: !!this.selectedDate.errors;

		if (!!control.errors && "invalidDateFormat" in control.errors) {
			const { invalidDateFormat, ...rest } = control.errors;
			control.setErrors(invalidDateFormat && JSON.stringify(rest) === "{}" ? null : rest);
		}

		this.isInvalid =
			isSelectedDateInvalid ||
			this.isInputDirty ||
			(JSON.stringify(control.errors) !== "{}" &&
				!!control.errors &&
				(control.touched || control.dirty));

		return this.isInvalid ? { invalidDateFormat: true } : null;
	}
}
